import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Button, StatCard, Table, useTable, Select, Input } from "@repo/o37-ui";
import {
  Plus,
  Target,
  Building,
  Calendar,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  Check,
  AlertTriangle,
  X
} from "lucide-react";
import { HoldingsCompany, formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface RevenueCategory {
  id: string;
  key: string;
  name: string;
  shortcutKey: string;
  color: string;
  sortOrder: number;
}

interface RevenueForecast {
  id: string;
  companyId: string;
  category: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  expectedCents: number;
  actualCents: number;
  notes?: string;
  company?: { id: string; name: string };
}

export default function RevenueForecastList() {
  const [forecasts, setForecasts] = useState<RevenueForecast[]>([]);
  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [categories, setCategories] = useState<RevenueCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingForecast, setEditingForecast] = useState<RevenueForecast | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });

  // Filters
  const [filterCompany, setFilterCompany] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPeriod, setFilterPeriod] = useState(() => {
    const now = new Date();
    const quarter = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${quarter} ${now.getFullYear()}`;
  });

  // Form state
  const [formData, setFormData] = useState({
    companyId: '',
    category: 'retainer',
    periodType: 'quarter',
    periodStart: '',
    expectedCents: 0,
    notes: ''
  });

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [forecastRes, companiesRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/holdings/revenue-forecasts`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/companies`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/revenue-categories`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (forecastRes.ok) {
        const data = await forecastRes.json();
        setForecasts(data.map((f: any) => ({
          id: f.id,
          companyId: f.company_id,
          category: f.category,
          periodType: f.period_type,
          periodStart: f.period_start,
          periodEnd: f.period_end,
          expectedCents: f.expected_cents,
          actualCents: f.actual_cents || 0,
          notes: f.notes,
          company: f.company
        })));
      }

      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setCompanies(data.map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status
        })));
      }

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data.map((c: any) => ({
          id: c.id,
          key: c.key,
          name: c.name,
          shortcutKey: c.shortcut_key,
          color: c.color,
          sortOrder: c.sort_order
        })));
      }
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this forecast? Any matched transactions will be unlinked.')) return;

    try {
      const response = await fetch(`${API_URL}/holdings/revenue-forecasts/${id}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to delete forecast:', e);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingForecast
        ? `${API_URL}/holdings/revenue-forecasts/${editingForecast.id}`
        : `${API_URL}/holdings/revenue-forecasts`;

      const response = await fetch(url, {
        method: editingForecast ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowForm(false);
        setEditingForecast(null);
        setFormData({
          companyId: '',
          category: 'retainer',
          periodType: 'quarter',
          periodStart: '',
          expectedCents: 0,
          notes: ''
        });
        loadData();
      }
    } catch (e) {
      console.error('Failed to save forecast:', e);
    }
  };

  const openEditForm = (forecast: RevenueForecast) => {
    setEditingForecast(forecast);
    setFormData({
      companyId: forecast.companyId,
      category: forecast.category,
      periodType: forecast.periodType,
      periodStart: forecast.periodStart,
      expectedCents: forecast.expectedCents,
      notes: forecast.notes || ''
    });
    setShowForm(true);
  };

  const openNewForm = () => {
    setEditingForecast(null);
    // Set default period start to first day of current quarter
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);

    setFormData({
      companyId: '',
      category: 'retainer',
      periodType: 'quarter',
      periodStart: quarterStart.toISOString().split('T')[0],
      expectedCents: 0,
      notes: ''
    });
    setShowForm(true);
  };

  // Generate period options
  const periodOptions = useMemo(() => {
    const options: string[] = [];
    const currentYear = new Date().getFullYear();

    // Add quarters for current and next year
    for (let year = currentYear - 1; year <= currentYear + 1; year++) {
      for (let q = 1; q <= 4; q++) {
        options.push(`Q${q} ${year}`);
      }
    }

    return options;
  }, []);

  // Parse period filter
  const periodFilter = useMemo(() => {
    if (!filterPeriod) return null;
    const match = filterPeriod.match(/Q(\d) (\d{4})/);
    if (!match) return null;

    const quarter = parseInt(match[1]);
    const year = parseInt(match[2]);
    const startMonth = (quarter - 1) * 3;

    return {
      start: new Date(year, startMonth, 1).toISOString().split('T')[0],
      end: new Date(year, startMonth + 3, 0).toISOString().split('T')[0]
    };
  }, [filterPeriod]);

  // Filter forecasts
  const filteredForecasts = useMemo(() => {
    return forecasts.filter(f => {
      if (filterCompany && f.companyId !== filterCompany) return false;
      if (filterCategory && f.category !== filterCategory) return false;
      if (periodFilter) {
        if (f.periodStart < periodFilter.start || f.periodStart > periodFilter.end) return false;
      }
      return true;
    });
  }, [forecasts, filterCompany, filterCategory, periodFilter]);

  // Calculate totals
  const totalExpected = filteredForecasts.reduce((sum, f) => sum + f.expectedCents, 0);
  const totalActual = filteredForecasts.reduce((sum, f) => sum + f.actualCents, 0);
  const totalVariance = totalActual - totalExpected;
  const variancePercent = totalExpected > 0 ? (totalVariance / totalExpected) * 100 : 0;

  // Count by status
  const onTrack = filteredForecasts.filter(f => {
    const variance = f.actualCents - f.expectedCents;
    const pct = f.expectedCents > 0 ? (variance / f.expectedCents) * 100 : 0;
    return pct >= -10 && pct <= 10;
  }).length;

  const getCategoryColor = (key: string) => {
    const cat = categories.find(c => c.key === key);
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700',
      green: 'bg-emerald-100 text-emerald-700',
      purple: 'bg-purple-100 text-purple-700',
      orange: 'bg-orange-100 text-orange-700',
      teal: 'bg-teal-100 text-teal-700',
      pink: 'bg-pink-100 text-pink-700',
      yellow: 'bg-amber-100 text-amber-700',
      gray: 'bg-neutral-100 text-neutral-700'
    };
    return colorMap[cat?.color || 'gray'] || colorMap.gray;
  };

  const getCategoryName = (key: string) => {
    return categories.find(c => c.key === key)?.name || key;
  };

  const formatPeriod = (periodStart: string, periodType: string) => {
    const start = new Date(periodStart);
    if (periodType === 'month') {
      return start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else if (periodType === 'quarter') {
      const quarter = Math.ceil((start.getMonth() + 1) / 3);
      return `Q${quarter} ${start.getFullYear()}`;
    } else {
      return start.getFullYear().toString();
    }
  };

  const columns = useMemo(() => [
    {
      id: "company",
      header: "Company",
      cell: ({ row }: { row: { original: RevenueForecast } }) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
            <Building className="w-5 h-5 text-neutral-500" />
          </div>
          <span className="font-medium text-neutral-900">
            {row.original.company?.name || 'Unknown'}
          </span>
        </div>
      )
    },
    {
      id: "category",
      header: "Category",
      cell: ({ row }: { row: { original: RevenueForecast } }) => (
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${getCategoryColor(row.original.category)}`}>
          {getCategoryName(row.original.category)}
        </span>
      )
    },
    {
      id: "period",
      header: "Period",
      cell: ({ row }: { row: { original: RevenueForecast } }) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-900">
            {formatPeriod(row.original.periodStart, row.original.periodType)}
          </span>
        </div>
      )
    },
    {
      id: "expected",
      header: "Expected",
      cell: ({ row }: { row: { original: RevenueForecast } }) => (
        <span className="text-neutral-600">
          {formatCents(row.original.expectedCents)}
        </span>
      )
    },
    {
      id: "actual",
      header: "Actual",
      cell: ({ row }: { row: { original: RevenueForecast } }) => (
        <span className="font-semibold text-emerald-600">
          {formatCents(row.original.actualCents)}
        </span>
      )
    },
    {
      id: "variance",
      header: "Variance",
      cell: ({ row }: { row: { original: RevenueForecast } }) => {
        const variance = row.original.actualCents - row.original.expectedCents;
        const pct = row.original.expectedCents > 0
          ? (variance / row.original.expectedCents) * 100
          : 0;
        const isPositive = variance >= 0;
        const isOnTrack = Math.abs(pct) <= 10;

        return (
          <div className="flex items-center gap-2">
            {isOnTrack ? (
              <Check className="w-4 h-4 text-emerald-500" />
            ) : isPositive ? (
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={isPositive ? 'text-emerald-600' : 'text-red-600'}>
              {isPositive ? '+' : ''}{formatCents(variance)}
            </span>
            <span className={`text-xs ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
              ({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)
            </span>
          </div>
        );
      }
    },
    {
      id: "progress",
      header: "Progress",
      cell: ({ row }: { row: { original: RevenueForecast } }) => {
        const progress = row.original.expectedCents > 0
          ? Math.min(100, (row.original.actualCents / row.original.expectedCents) * 100)
          : 0;
        const isOver = row.original.actualCents > row.original.expectedCents;

        return (
          <div className="w-24">
            <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  isOver ? 'bg-emerald-500' : progress >= 90 ? 'bg-emerald-400' : progress >= 50 ? 'bg-amber-400' : 'bg-red-400'
                }`}
                style={{ width: `${Math.min(100, progress)}%` }}
              />
            </div>
            <span className="text-xs text-neutral-500 mt-1">
              {progress.toFixed(0)}%
            </span>
          </div>
        );
      }
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }: { row: { original: RevenueForecast } }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-8 text-xs px-2"
            onClick={() => openEditForm(row.original)}
            icon={<Edit2 className="w-3.5 h-3.5" />}
            text=""
          />
          <Button
            variant="outline"
            className="h-8 text-xs px-2 text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => handleDelete(row.original.id)}
            icon={<Trash2 className="w-3.5 h-3.5" />}
            text=""
          />
        </div>
      )
    }
  ], [categories]);

  const { table, ...tableProps } = useTable({
    data: filteredForecasts,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: filteredForecasts.length,
    resourceName: (plural) => `forecast${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Revenue Forecasts</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Revenue Forecasts</h1>
          <p className="text-neutral-500">Track expected vs actual revenue by company</p>
        </div>
        <Button
          onClick={openNewForm}
          icon={<Plus className="w-4 h-4" />}
          text="Add Forecast"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Expected"
          value={formatCents(totalExpected)}
          hint={filterPeriod || 'All time'}
        />
        <StatCard
          label="Actual"
          value={formatCents(totalActual)}
          hint="Revenue received"
        />
        <StatCard
          label="Variance"
          value={`${variancePercent >= 0 ? '+' : ''}${variancePercent.toFixed(1)}%`}
          hint={variancePercent >= 0 ? 'Above target' : 'Below target'}
        />
        <StatCard
          label="On Track"
          value={`${onTrack}/${filteredForecasts.length}`}
          hint="Within 10% variance"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-48">
          <Select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)}>
            <option value="">All Periods</option>
            {periodOptions.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
        </div>
        <div className="w-48">
          <Select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </div>
        <div className="w-48">
          <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.key} value={c.key}>{c.name}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* Forecasts Table */}
      <Card>
        <CardContent className="p-0">
          {filteredForecasts.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Target className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <h3 className="text-lg font-medium mb-2">No Revenue Forecasts</h3>
              <p className="mb-6">
                {filterPeriod || filterCompany || filterCategory
                  ? 'No forecasts match your filters.'
                  : 'Create forecasts to track expected vs actual revenue.'}
              </p>
              <Button
                onClick={openNewForm}
                icon={<Plus className="w-4 h-4" />}
                text="Add Forecast"
              />
            </div>
          ) : (
            <Table {...tableProps} table={table} />
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            How Revenue Forecasts Work
          </h3>
          <div className="text-sm text-neutral-600 space-y-2">
            <p>
              <strong>Creating forecasts:</strong> Set expected revenue for a company, category, and time period.
              For example: "Acme Corp - Retainer - Q1 2025 - $30,000"
            </p>
            <p>
              <strong>Auto-matching:</strong> When you categorize an incoming revenue transaction with a company
              and category (in the Transactions view), it automatically matches to the appropriate forecast
              and updates the actual amount.
            </p>
            <p>
              <strong>Keyboard shortcuts:</strong> In the Transactions view, press a number key (1-9) to select
              a company, then a letter key (R/S/O/P/C/M/L/X) for the revenue category.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Forecast Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-neutral-100">
              <h2 className="text-xl font-semibold">
                {editingForecast ? 'Edit Forecast' : 'Add Revenue Forecast'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingForecast(null);
                }}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Company</label>
              <Select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              >
                <option value="">Select company...</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map(c => (
                  <option key={c.key} value={c.key}>{c.name} ({c.shortcutKey})</option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Period Type</label>
                <Select
                  value={formData.periodType}
                  onChange={(e) => setFormData({ ...formData, periodType: e.target.value })}
                >
                  <option value="month">Month</option>
                  <option value="quarter">Quarter</option>
                  <option value="year">Year</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Period Start</label>
                <Input
                  type="date"
                  value={formData.periodStart}
                  onChange={(e) => setFormData({ ...formData, periodStart: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Expected Amount ($)</label>
              <Input
                type="number"
                step="0.01"
                value={(formData.expectedCents / 100).toFixed(2)}
                onChange={(e) => setFormData({
                  ...formData,
                  expectedCents: Math.round(parseFloat(e.target.value || '0') * 100)
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g., Monthly retainer for consulting services"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingForecast(null);
                }}
                text="Cancel"
              />
              <Button
                onClick={handleSubmit}
                disabled={!formData.companyId || !formData.category || !formData.periodStart || formData.expectedCents <= 0}
                text={editingForecast ? 'Save Changes' : 'Create Forecast'}
              />
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
